<template>
  <div class="machine-page">
    <div class="head">
      <h2>머신 기록</h2>
      <p class="desc">
        수업 뒤에 회원별 머신 기록을 입력하거나 지난 기록을 고쳐요. 날짜와
        수업을 고르면 출석 처리한 회원이 나와요. 저장하면 회원 앱의 퍼포먼스
        기록과 등급에 반영돼요.
      </p>
    </div>

    <!-- 날짜: 그 주 월~일 탭, 이전·다음 주, 날짜 바로 고르기 -->
    <div class="date-bar">
      <div class="week">
        <button
          v-for="d in days"
          :key="d.date"
          type="button"
          :class="['day', { sel: d.date === date }]"
          @click="pickDate(d.date)"
        >
          <span class="dow">{{ d.dow }}</span>
          <span class="num">{{ d.num }}</span>
        </button>
      </div>
      <div class="week-nav">
        <button type="button" class="nav-btn" @click="moveWeek(-1)">
          ← 이전 주
        </button>
        <input
          ref="dateInput"
          class="date-input"
          type="date"
          aria-label="날짜 고르기"
          @change="onDateInput($event)"
          @blur="onDateBlur($event)"
        />
        <button type="button" class="nav-btn" @click="moveWeek(1)">
          다음 주 →
        </button>
      </div>
    </div>

    <!-- 그날 수업 -->
    <div class="class-list" aria-label="수업">
      <p v-if="scheduleError" class="empty error" role="alert">
        {{ scheduleError }}
        <button type="button" class="link-btn" @click="loadSchedule">
          다시 불러오기
        </button>
      </p>
      <p v-else-if="loadingSchedule" class="empty">불러오는 중…</p>
      <p v-else-if="schedule.length === 0" class="empty">
        이 날짜에는 수업이 없어요.
      </p>
      <button
        v-for="cls in schedule"
        :key="cls.id"
        type="button"
        :class="['class-item', { active: cls.id === classId }]"
        :data-class-id="cls.id"
        @click="pickClass(cls)"
      >
        <span class="class-name"
          >{{ hhmm(cls.time) }} {{ typeLabelOf(cls) }}</span
        >
        <span class="class-meta">{{ cls.booked }} / {{ cls.capacity }}명</span>
      </button>
    </div>

    <section v-if="selectedClass" class="form-panel">
      <MachineRecordForm
        :key="selectedClass.id"
        :class-id="selectedClass.id"
        :class-title="typeLabelOf(selectedClass)"
        :class-label="classLabelOf(selectedClass)"
        @dirty-change="formDirty = $event"
      />
    </section>
    <p v-else-if="schedule.length" class="empty">
      기록을 입력할 수업을 골라 주세요.
    </p>
  </div>
</template>

<script>
import MachineRecordForm from "@/components/MachineRecordForm.vue";
import { fetchSchedule } from "@/lib/classAdminApi";
import { formatClassLabel } from "@/lib/machineCatalog";
import {
  classDisplayName,
  hhmm,
  isYmd,
  weekDays,
  withClassDate,
} from "@/lib/classTypes";

const DOW = ["일", "월", "화", "수", "목", "금", "토"];
const DISCARD_MESSAGE =
  "저장하지 않은 머신 기록이 있어요. 입력을 버리고 넘어갈까요?";

function toYmd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseYmd(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

// 같은 이름이 여러 번 오면 첫 값만 본다
function queryValue(v) {
  const first = Array.isArray(v) ? v[0] : v;
  return first == null ? "" : String(first);
}

// R7 머신 기록 화면(docs/architecture/app-v2/R6b-R7.md 2.4 위치 2): 날짜 → 그날 수업(GET /v1/admin/classes/schedule) → 수업 →
// MachineRecordForm(GET /v1/admin/classes/{class_id}/machine-records로 기존 값 채움). 수업 뒤 입력과 지난 기록 수정(6.7)용.
// 주소 ?date=YYYY-MM-DD&class_id= 로 같은 수업을 다시 연다(실시간 심박 저장 패널의 "나중에 입력" 링크도 이 주소)
export default {
  name: "MachineRecordsView",
  components: { MachineRecordForm },
  beforeRouteUpdate(to) {
    // 이 화면이 스스로 바꾼 주소(날짜·수업 고르기)는 고르기 전에 이미 확인했다
    return this.queryMatchesState(to.query) || this.confirmDiscard();
  },
  beforeRouteLeave() {
    return this.confirmDiscard();
  },
  data() {
    return {
      date: "",
      classId: "",
      schedule: [],
      loadingSchedule: false,
      scheduleError: "",
      scheduleSeq: 0,
      formDirty: false,
    };
  },
  computed: {
    days() {
      const base = parseYmd(this.date) || new Date();
      return weekDays(base, 0).map((dt) => ({
        date: toYmd(dt),
        dow: DOW[dt.getDay()],
        num: String(dt.getDate()),
      }));
    },
    selectedClass() {
      return this.schedule.find((c) => c.id === this.classId) || null;
    },
  },
  watch: {
    // 사이드 메뉴(/machine-records)나 뒤로 가기로 주소가 바뀌면 그 주소대로 보인다
    "$route.query"(query) {
      if (this.$route.name !== "MachineRecords") return;
      if (!this.queryMatchesState(query)) this.applyQuery(query);
    },
    // 날짜 입력칸은 값을 템플릿에 묶지 않고 날짜가 바뀔 때 여기서 쓴다(syncDateInput)
    date() {
      this.syncDateInput();
    },
  },
  mounted() {
    this.applyQuery(this.$route.query);
    this.syncDateInput();
  },
  methods: {
    hhmm,
    // 수업 이름: 타입 표시명, 기타 수업은 관리자가 정한 이름(classDisplayName, 2026-10-24 결정 B·DECISIONS CT-15). 실시간 심박 저장
    // 패널(RealtimeHrView typeLabelOf·machineClass)과 같은 함수라 같은 수업은 두 입구에서 폼 머리와 머신 선택 기억 키가 같다
    typeLabelOf(cls) {
      return classDisplayName(cls);
    },
    // "10월 17일 (토) 19:30 Pull & Cardio", 기타 수업은 "11월 1일 (일) 10:00 북한산 등산"
    classLabelOf(cls) {
      return formatClassLabel(
        cls.date || this.date,
        hhmm(cls.time),
        this.typeLabelOf(cls)
      );
    },
    confirmDiscard() {
      if (!this.formDirty) return true;
      return window.confirm(DISCARD_MESSAGE);
    },
    queryMatchesState(query) {
      const date = queryValue(query.date);
      const classId = queryValue(query.class_id);
      return (
        (isYmd(date) ? date : toYmd(new Date())) === this.date &&
        classId === this.classId
      );
    },
    applyQuery(query) {
      const date = queryValue(query.date);
      const nextDate = isYmd(date) ? date : toYmd(new Date());
      const nextClass = queryValue(query.class_id);
      const dateChanged = nextDate !== this.date;
      this.date = nextDate;
      this.classId = nextClass;
      this.formDirty = false;
      if (dateChanged || !this.schedule.length) this.loadSchedule();
    },
    syncQuery() {
      const query = { date: this.date };
      if (this.classId) query.class_id = this.classId;
      this.$router.replace({ query }).catch(() => {});
    },
    pickDate(ymd) {
      if (!isYmd(ymd) || ymd === this.date) return;
      if (!this.confirmDiscard()) return;
      this.date = ymd;
      this.classId = "";
      this.formDirty = false;
      this.syncQuery();
      this.loadSchedule();
    },
    // 날짜 입력칸에 지금 날짜를 쓴다(이미 같으면 쓰지 않음). 입력칸 값은 템플릿에 묶지 않는다: :value="date"로 묶으면 Vue가 이
    // 화면을 다시 그릴 때마다 입력칸 값이 date와 다르면 다시 써서, Chrome이 월·일 칸 첫 글자 "0"을 기다리는 동안(값 "") 늦게 온
    // 수업 목록 응답의 다시 그리기가 그 "0"을 버렸다(월 칸 0 1 1 5 → 2026-11-05, R7 전체 리뷰 6차). 그래서 날짜가 바뀔 때(date
    // 감시자, 처음 그릴 때)만 쓰고, 확인 창 취소와 떠날 때는 아래 두 처리가 되돌린다
    syncDateInput() {
      const input = this.$refs.dateInput;
      if (input && input.value !== this.date) input.value = this.date;
    },
    // 고른 날짜를 확인 창에서 취소하면(pickDate가 날짜를 그대로 둠) date가 바뀌지 않아 입력칸을 쓰는 곳이 없다. 그대로 두면 입력칸만
    // 새 날짜이고 주 탭·주소·폼은 옛 날짜였다(R7 전체 리뷰 1차). 그래서 그때 지금 날짜로 되돌린다.
    // 날짜가 아닌 값(빈 값)은 change에서 건드리지 않는다: Chrome은 월·일 칸에 첫 글자 "0"만 친 동안에도 값 ""로 change를 보내,
    // 그때 되돌리면 기다리던 "0"이 버려지고 다음 숫자가 같은 칸에 붙어 엉뚱한 날짜로 갔다(R7 전체 리뷰 5차). 지운 칸은 입력칸을
    // 떠날 때(onDateBlur) 되돌린다. 첫 글자 "0"만 친 채 칸을 떠나면 Chrome이 blur 전에 그 칸을 01(연도 칸은 0001)로 채워 날짜
    // change를 보내므로 화면은 그 날짜로 간다(맨 date 입력칸과 같음, R7 전체 리뷰 6차에서 설명을 바로잡음)
    onDateInput(e) {
      const input = e && e.target;
      if (!input) return;
      this.pickDate(input.value);
      if (isYmd(input.value) && input.value !== this.date)
        input.value = this.date;
    },
    onDateBlur(e) {
      const input = e && e.target;
      if (input && input.value !== this.date) input.value = this.date;
    },
    moveWeek(step) {
      const d = parseYmd(this.date) || new Date();
      d.setDate(d.getDate() + step * 7);
      this.pickDate(toYmd(d));
    },
    pickClass(cls) {
      if (!cls || cls.id === this.classId) return;
      if (!this.confirmDiscard()) return;
      this.classId = cls.id;
      this.formDirty = false;
      this.syncQuery();
    },
    async loadSchedule() {
      // 날짜를 빨리 옮기면 앞 요청의 응답이 나중에 올 수 있다. 마지막 요청의 응답만 쓴다
      const seq = ++this.scheduleSeq;
      const date = this.date;
      this.loadingSchedule = true;
      this.scheduleError = "";
      try {
        const classes = await fetchSchedule(date);
        if (seq !== this.scheduleSeq) return;
        this.schedule = withClassDate(classes, date);
      } catch (e) {
        if (seq !== this.scheduleSeq) return;
        this.schedule = [];
        this.scheduleError =
          e && e.status === 0
            ? "서버에 연결하지 못했어요. 잠시 뒤 다시 불러와 주세요."
            : `수업 목록을 불러오지 못했어요 (오류 ${e && e.status}).`;
      } finally {
        if (seq === this.scheduleSeq) this.loadingSchedule = false;
      }
    },
  },
};
</script>

<style scoped>
.machine-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  /* 폼 표의 최소 너비가 MainLayout의 1fr 칸을 밀지 않게(TrendsView와 같은 방법) */
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
  color: #fff;
}
.head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
}
/* 줄을 바꾸는 한국어 문구(.desc, .empty)는 모든 폭에서 낱말 사이에서만 줄을 바꾼다(띄어쓰기 없는 긴 글만 어디서든 끊는다,
   MachineRecordForm과 같은 규칙. 예전에는 390px에서 "고/쳐요"처럼 끊겼다, R7 전체 리뷰 4차) */
.desc {
  margin: 0;
  color: #8e8e8e;
  font-size: 14px;
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.date-bar {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.week {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.day {
  padding: 8px 4px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1b1d1e;
  color: #c8c8c8;
  text-align: center;
  cursor: pointer;
}
.day.sel {
  background: #78ff47;
  border-color: #78ff47;
  color: #000;
}
.dow {
  display: block;
  font-size: 11px;
}
.num {
  display: block;
  font-size: 16px;
  font-weight: 700;
}
.week-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.nav-btn {
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.nav-btn:hover {
  background: #252728;
}
.date-input {
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 14px;
  color-scheme: dark;
}

.class-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}
.class-list .empty {
  grid-column: 1 / -1;
}
.class-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
  text-align: left;
}
.class-item:hover:not(.active) {
  background: #252728;
}
.class-item.active {
  border-color: #78ff47;
  box-shadow: 0 0 0 1px rgba(120, 255, 71, 0.3);
}
.class-name {
  font-size: 14px;
  font-weight: 600;
}
.class-meta {
  color: #8e8e8e;
  font-size: 12px;
  white-space: nowrap;
}

.form-panel {
  border-top: 1px solid #2a2a2a;
  padding-top: 16px;
  min-width: 0;
}

.empty {
  margin: 0;
  padding: 12px;
  color: #c8c8c8;
  font-size: 14px;
  word-break: keep-all;
  overflow-wrap: anywhere;
}
.empty.error {
  color: #ff8a8b;
}
.link-btn {
  background: none;
  border: 0;
  padding: 0;
  margin-left: 6px;
  color: #78ff47;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
}

@media (max-width: 640px) {
  .week-nav {
    flex-wrap: wrap;
  }
}
</style>
